import { Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Copy, CheckCircle, Zap, Workflow, Boxes, Code, Settings } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { BrandIcons } from '../../components/ui/brand-icons';
import SEOHead from '../../components/SEOHead';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import MemoryTypesBadges from '@/components/ui/MemoryTypesBadges';

const N8nIcon = BrandIcons.n8n;

export default function N8NLanding() {
  const { t } = useTranslation('integrations');
  const [copied, setCopied] = useState(false);

  const handleCopyInstall = () => {
    navigator.clipboard.writeText('npm install n8n-nodes-memara');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-pink-50">
      <SEOHead
        title="n8n AI Memory: Memara Node for Persistent Memory"
        description="Add persistent AI memory to n8n with the Memara community node. Store, search, and retrieve memories across workflows with multi-tenant architecture and semantic search."
        keywords={[
          'n8n ai memory',
          'n8n memory',
          'n8n mem0',
          'mem0 n8n',
          'simple memory n8n',
          'n8n community node',
          'persistent ai memory',
          'multi-tenant n8n',
          'memara n8n',
          'n8n nodes memara'
        ]}
        ogType="article"
        priority="high"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'n8n-nodes-memara',
          applicationCategory: 'Automation',
          operatingSystem: 'Cross-platform',
          offers: { '@type': 'Offer', price: 0, priceCurrency: 'USD' },
          description:
            'Memara community node for n8n that adds persistent AI memory with semantic search to your workflows. Supports create, search, update, delete, and list operations.'
        }}
      />

      <div className="sr-only">
        Keywords: n8n AI memory, simple memory n8n, mem0 n8n, n8n mem0, persistent AI memory, multi-tenant n8n memory, Memara n8n nodes, n8n community node for memory.
      </div>

      {/* Navigation */}
      <nav className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/integrations" className="flex items-center text-gray-600 hover:text-gray-900 transition-colors">
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Integrations
            </Link>
            <div className="flex items-center space-x-4">
              <Badge variant="secondary" className="bg-green-100 text-green-800">
                Available Now
              </Badge>
              <Button asChild size="sm">
                <Link to="/signup">Get Started</Link>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-16 pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center mb-8">
              <div className="flex items-center space-x-4 p-6 bg-white rounded-2xl shadow-lg border">
                <N8nIcon className="w-16 h-16" />
                <div className="w-8 h-px bg-gradient-to-r from-red-500 to-pink-600"></div>
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-xl">M</span>
                </div>
              </div>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Memara + <span className="text-red-600">n8n</span>
            </h1>
            
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Connect your entire tech stack to Memara using n8n's visual workflows. 
              Store, search, and retrieve memories across 400+ integrations with fair-code automation.
            </p>

            <div className="max-w-2xl mx-auto mb-10 border border-gray-200 rounded-xl bg-white/90 px-4 py-5 shadow-sm">
              <h2 className="text-sm font-semibold text-gray-800 mb-3 text-center">
                {t('memoryModalities.sectionTitle')}
              </h2>
              <MemoryTypesBadges integrationId="n8n" className="justify-center" />
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <div className="flex items-center bg-gray-900 text-white px-4 py-2 rounded-lg font-mono text-sm">
                <span>npm install n8n-nodes-memara</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCopyInstall}
                  className="ml-2 h-8 w-8 p-0 text-white hover:bg-gray-700"
                >
                  {copied ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              
              <Button asChild className="bg-red-600 hover:bg-red-700">
                <a href="https://www.npmjs.com/package/n8n-nodes-memara" target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View on npm
                </a>
              </Button>
            </div>

            <p className="text-sm text-gray-500 mt-4 max-w-2xl mx-auto">
              Multiple installation methods available: verified nodes, GUI installation, or manual setup
            </p>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Fair-Code Automation with Memory
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              n8n's ethical approach to automation meets Memara's persistent memory system
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center p-6 rounded-lg border bg-gradient-to-br from-red-50 to-pink-50">
              <div className="w-12 h-12 mx-auto mb-4 bg-red-100 rounded-lg flex items-center justify-center">
                <Workflow className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Visual Workflows</h3>
              <p className="text-gray-600 text-sm">
                Build memory-powered automations with n8n's intuitive visual interface
              </p>
            </div>

            <div className="text-center p-6 rounded-lg border bg-gradient-to-br from-orange-50 to-red-50">
              <div className="w-12 h-12 mx-auto mb-4 bg-orange-100 rounded-lg flex items-center justify-center">
                <Boxes className="h-6 w-6 text-orange-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">400+ Integrations</h3>
              <p className="text-gray-600 text-sm">
                Connect any service to Memara through n8n's extensive integration ecosystem
              </p>
            </div>

            <div className="text-center p-6 rounded-lg border bg-gradient-to-br from-purple-50 to-red-50">
              <div className="w-12 h-12 mx-auto mb-4 bg-purple-100 rounded-lg flex items-center justify-center">
                <Code className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Fair-Code License</h3>
              <p className="text-gray-600 text-sm">
                Self-host or use n8n Cloud while maintaining full control over your data
              </p>
            </div>

            <div className="text-center p-6 rounded-lg border bg-gradient-to-br from-blue-50 to-purple-50">
              <div className="w-12 h-12 mx-auto mb-4 bg-blue-100 rounded-lg flex items-center justify-center">
                <Zap className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Memory Operations</h3>
              <p className="text-gray-600 text-sm">
                Full CRUD operations: Create, Search, Update, Delete, and List memories
              </p>
            </div>
          </div>
        </div>
      </section>

                {/* Installation Guide */}
          <section className="py-24 bg-gray-50">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-16">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Installation Methods
                </h2>
                <p className="text-xl text-gray-600">
                  Choose the installation method that works best for your n8n setup
                </p>
              </div>

              {/* Installation Methods Grid */}
              <div className="grid md:grid-cols-3 gap-6 mb-12">
                {/* Method 1: Verified Community Node */}
                <div className="bg-white p-6 rounded-lg shadow-sm border-2 border-green-200">
                  <div className="text-center mb-4">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-green-900">Verified Node</h3>
                    <Badge variant="secondary" className="bg-green-100 text-green-800 text-xs">
                      Easiest Method
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Install directly from n8n's nodes panel (for verified community nodes)
                  </p>
                  <div className="space-y-2 text-sm text-gray-600">
                    <p>• Open nodes panel (+ or Tab)</p>
                    <p>• Search for "Memara"</p>
                    <p>• Click Install in node details</p>
                    <p>• Available immediately</p>
                  </div>
                </div>

                {/* Method 2: GUI Installation */}
                <div className="bg-white p-6 rounded-lg shadow-sm border-2 border-blue-200">
                  <div className="text-center mb-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Settings className="w-6 h-6 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-blue-900">GUI Installation</h3>
                    <Badge variant="secondary" className="bg-blue-100 text-blue-800 text-xs">
                      Self-Hosted
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Install from npm registry through n8n's interface
                  </p>
                  <div className="space-y-2 text-sm text-gray-600">
                    <p>• Go to Settings → Community Nodes</p>
                    <p>• Click Install</p>
                    <p>• Enter: n8n-nodes-memara</p>
                    <p>• Restart n8n instance</p>
                  </div>
                </div>

                {/* Method 3: Manual Installation */}
                <div className="bg-white p-6 rounded-lg shadow-sm border-2 border-purple-200">
                  <div className="text-center mb-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Code className="w-6 h-6 text-purple-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-purple-900">Manual Install</h3>
                    <Badge variant="secondary" className="bg-purple-100 text-purple-800 text-xs">
                      Advanced
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Command line installation for queue mode or private packages
                  </p>
                  <div className="bg-gray-900 text-white p-2 rounded font-mono text-xs mb-2">
                    docker exec -it n8n sh
                  </div>
                  <div className="bg-gray-900 text-white p-2 rounded font-mono text-xs">
                    npm i n8n-nodes-memara
                  </div>
                </div>
              </div>

              {/* Common Setup Steps */}
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    After Installation
                  </h3>
                  <p className="text-gray-600">
                    Complete these steps regardless of your installation method
                  </p>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-red-500">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                      <span className="text-red-600 font-semibold">1</span>
                    </div>
                    <div className="flex-grow">
                      <h3 className="text-lg font-semibold mb-2">Choose Your Installation Method</h3>
                      <p className="text-gray-600 mb-3">
                        Select the method above that matches your n8n setup and deployment type
                      </p>
                    </div>
                  </div>
                </div>

                            <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-orange-500">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                      <span className="text-orange-600 font-semibold">2</span>
                    </div>
                    <div className="flex-grow">
                      <h3 className="text-lg font-semibold mb-2">Configure Memara Credentials</h3>
                      <p className="text-gray-600 mb-3">
                        Add your Memara API credentials in n8n's credential settings
                      </p>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• Go to Settings → Credentials in n8n</li>
                        <li>• Create new "Memara API" credential</li>
                        <li>• API Base URL: <code className="bg-gray-100 px-1">https://api.memara.io</code></li>
                        <li>• API Key: Get from your Memara dashboard</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-purple-500">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                      <span className="text-purple-600 font-semibold">3</span>
                    </div>
                    <div className="flex-grow">
                      <h3 className="text-lg font-semibold mb-2">Create Your First Workflow</h3>
                      <p className="text-gray-600 mb-3">
                        Start with a simple workflow to test the integration
                      </p>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• Add the Memara node you need (for example Create Memory or Search Memories) or a Memara trigger</li>
                        <li>• Select the Memara API credential</li>
                        <li>• Configure the memory content and metadata</li>
                        <li>• Test and deploy your automation</li>
                      </ul>
                    </div>
                  </div>
                </div>
          </div>

          <div className="mt-12 text-center">
            <Button asChild size="lg" className="bg-red-600 hover:bg-red-700">
              <Link to="/signup">
                <Zap className="h-5 w-5 mr-2" />
                Start Building with Memara + n8n
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Automation Use Cases
            </h2>
            <p className="text-xl text-gray-600">
              See what you can build with Memara + n8n
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="p-6 rounded-lg border hover:shadow-lg transition-shadow">
              <h3 className="text-lg font-semibold mb-3">Customer Support Knowledge Base</h3>
              <p className="text-gray-600 text-sm mb-4">
                Automatically capture support tickets, categorize issues, and build a searchable knowledge base for your team.
              </p>
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary" className="text-xs">Slack</Badge>
                <Badge variant="secondary" className="text-xs">Email</Badge>
                <Badge variant="secondary" className="text-xs">Memory Search</Badge>
              </div>
            </div>

            <div className="p-6 rounded-lg border hover:shadow-lg transition-shadow">
              <h3 className="text-lg font-semibold mb-3">Lead Intelligence System</h3>
              <p className="text-gray-600 text-sm mb-4">
                Gather lead information from multiple sources and create comprehensive prospect profiles with interaction history.
              </p>
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary" className="text-xs">CRM</Badge>
                <Badge variant="secondary" className="text-xs">LinkedIn</Badge>
                <Badge variant="secondary" className="text-xs">Email Tracking</Badge>
              </div>
            </div>

            <div className="p-6 rounded-lg border hover:shadow-lg transition-shadow">
              <h3 className="text-lg font-semibold mb-3">Content Research Assistant</h3>
              <p className="text-gray-600 text-sm mb-4">
                Monitor industry news, research topics, and build a personalized knowledge database for content creation.
              </p>
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary" className="text-xs">RSS</Badge>
                <Badge variant="secondary" className="text-xs">Twitter</Badge>
                <Badge variant="secondary" className="text-xs">Research</Badge>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-red-600 to-pink-600 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Automate with Memory?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Join thousands of developers building intelligent automations with n8n and Memara
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button asChild size="lg" variant="secondary" className="bg-white text-red-600 hover:bg-gray-100">
              <Link to="/signup">
                Start Free Trial
              </Link>
            </Button>
            
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-red-600">
              <a href="https://docs.n8n.io/integrations/community-nodes/" target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4 mr-2" />
                n8n Documentation
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
} 